<template>
  <nav
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled || !isHomePage
        ? 'bg-white/80 dark:bg-dark-bg/80 backdrop-blur-lg shadow-lg'
        : 'bg-transparent',
    ]"
  >
    <div class="container-custom">
      <div class="flex items-center justify-between h-20">
        <!-- Logo -->
        <a
          href="#hero"
          @click.prevent="handleLogoClick"
          class="flex items-center space-x-3 group cursor-pointer"
        >
          <img
            src="@/assets/logo-icon.webp"
            alt="Devolysis Logo"
            class="h-10 w-10 transition-transform duration-300 group-hover:scale-110"
          />
          <span
            class="text-2xl font-display font-bold text-gradient hidden sm:block"
          >
            Devolysis
          </span>
        </a>

        <!-- Desktop Navigation -->
        <div class="hidden lg:flex items-center space-x-8">
          <a
            v-for="item in navItems"
            :key="item.id"
            :href="item.href"
            @click.prevent="handleNavClick(item.href)"
            :class="[
              'text-sm font-medium transition-colors duration-200 relative group cursor-pointer',
              activeSection === item.href && isHomePage
                ? isScrolled || !isHomePage
                  ? 'text-primary dark:text-accent'
                  : 'text-white dark:text-accent'
                : isScrolled || !isHomePage
                ? 'text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-accent'
                : 'text-white/90 dark:text-gray-300 hover:text-white dark:hover:text-accent',
            ]"
          >
            {{ item.label }}
            <span
              :class="[
                'absolute bottom-0 left-0 w-full h-0.5 transform origin-left transition-transform duration-300',
                isScrolled || !isHomePage
                  ? 'bg-primary dark:bg-accent'
                  : 'bg-white dark:bg-accent',
                activeSection === item.href && isHomePage
                  ? 'scale-x-100'
                  : 'scale-x-0 group-hover:scale-x-100',
              ]"
            ></span>
          </a>

          <DarkModeToggle />

          <button @click="handleNavClick('#contact')" class="btn-primary">
            Get Started
          </button>
        </div>

        <!-- Mobile Menu Button -->
        <div class="lg:hidden flex items-center space-x-4">
          <DarkModeToggle />
          <button
            @click="toggleMobileMenu"
            class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Toggle menu"
          >
            <X
              v-if="isMobileMenuOpen"
              :size="28"
              class="text-gray-900 dark:text-gray-100"
            />
            <Menu v-else :size="28" class="text-gray-900 dark:text-gray-100" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition name="slide-down">
      <div
        v-if="isMobileMenuOpen"
        class="lg:hidden bg-white dark:bg-dark-surface border-t border-gray-200 dark:border-dark-border shadow-xl"
      >
        <div class="container-custom py-6 space-y-4">
          <a
            v-for="item in navItems"
            :key="item.id"
            :href="item.href"
            @click.prevent="handleMobileNavClick(item.href)"
            :class="[
              'block py-3 px-4 rounded-lg font-medium transition-colors duration-200',
              activeSection === item.href && isHomePage
                ? 'bg-primary/10 dark:bg-accent/10 text-primary dark:text-accent'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg',
            ]"
          >
            {{ item.label }}
          </a>

          <button
            @click="handleMobileNavClick('#contact')"
            class="w-full btn-primary"
          >
            Get Started
          </button>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Menu, X } from "lucide-vue-next";
import DarkModeToggle from "./DarkModeToggle.vue";
import { useSmoothScroll } from "@/composables/useSmoothScroll";

const route = useRoute();
const router = useRouter();
const { scrollToSection, getActiveSection } = useSmoothScroll();

const navItems = [
  { id: "about", label: "About", href: "#about" },
  { id: "services", label: "Services", href: "#services" },
  { id: "process", label: "Process", href: "#process" },
  { id: "portfolio", label: "Work", href: "#portfolio" },
  // { id: 'team', label: 'Team', href: '#team' },
  { id: "faq", label: "FAQ", href: "#faq" },
  { id: "contact", label: "Contact", href: "#contact" },
];

const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);
const activeSection = ref("#hero");

const isHomePage = computed(() => route.name === "Home");

const handleLogoClick = () => {
  if (isHomePage.value) {
    scrollToSection("#hero");
  } else {
    router.push("/");
  }
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const handleMobileNavClick = (href) => {
  if (isHomePage.value) {
    scrollToSection(href);
  } else {
    router.push("/" + href);
  }
  isMobileMenuOpen.value = false;
};

const handleNavClick = (href) => {
  if (isHomePage.value) {
    scrollToSection(href);
  } else {
    router.push("/" + href);
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;

  // Update active section only on home page
  if (isHomePage.value) {
    const sections = navItems.map((item) => item.href);
    activeSection.value = getActiveSection(sections);
  }
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
